<template>
	<div class="item page" v-if="item">
		<div class="container">
			<h2 class="title">{{ item.company }} {{ item.title }}</h2>

			<div class="settings">
				<div class="settings-box">
					<div class="settings-title">Style</div>
					<styles :styles="item.styles"></styles>
				</div>

				<div class="settings-box">
					<div class="settings-title">Color</div>
					<colors :colors="style.colors"></colors>
				</div>

				<div class="settings-box">
					<div class="settings-title">Background</div>
					<background
						:item="item"
						:backgroundColor="backgroundColor"
					></background>
				</div>
			</div>

			<div class="image-wrap">
				<div class="image" :style="{backgroundColor}">
					<mockup
						:single="true"
						:item="item"
						:maxHeight="height"
						:padding="padding"
						@loaded="loaded = true"
					></mockup>
				</div>
			</div>

			<div class="loading" v-if="!loaded">
				<div class="spinner">
					<div class="double-bounce1"></div>
					<div class="double-bounce2"></div>
				</div>
			</div>

			<template v-else>
				<div class="download">
					<transition name="download" mode="out-in">
						<a
							class="button"
							v-if="!downloading"
							:class="{disabled: !support}"
							@click="download"
							>Download</a
						>
						<div class="spinner" v-else>
							<div class="double-bounce1"></div>
							<div class="double-bounce2"></div>
						</div>
					</transition>
				</div>

				<div class="support" v-if="!support">
					iOS, Safari, Internet Explorer and MS Edge are not supported.<br />Try
					<a
						href="https://www.google.com/chrome/browser/desktop/index.html"
						target="_blank"
						>Chrome</a
					>,
					<a href="https://www.mozilla.org/en-US/firefox/new" target="_blank"
						>Firefox</a
					>
					or <a href="http://www.opera.com" target="_blank">Opera</a>
				</div>
			</template>
		</div>

		<div class="capture" id="capture" :style="{fontSize: captureSize}">
			<div class="image" :style="{backgroundColor}">
				<mockup
					:single="true"
					:item="item"
					:maxHeight="height"
					:padding="padding"
				></mockup>
			</div>
		</div>
	</div>
</template>

<script>
import jump from "jump.js";
import domtoimage from "dom-to-image";
import fileSaver from "file-saver";

import mockup from "../components/mockup.vue";
import styles from "../components/single/styles.vue";
import colors from "../components/single/colors.vue";
import background from "../components/single/background.vue";

export default {
	components: {mockup, styles, colors, background},
	data() {
		return {
			item: this.$route.meta.item,
			height: "28em",
			padding: "3em",
			captureHeight: 2000 / window.devicePixelRatio,
			downloading: false,
			loaded: false
		};
	},
	computed: {
		backgroundColors: (t) => t.$store.state.backgroundColors,
		transparent: (t) => t.$store.state.transparent,
		style: (t) => t.item.styles.find((x) => x.active),

		captureSize() {
			const height = Number(this.height.split("em")[0]) * 16;
			return this.captureHeight / height + "em";
		},

		backgroundColor() {
			if (this.transparent) return "transparent";
			const c = this.backgroundColors.rgba;
			return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
		},

		support() {
			let out = true;

			// if no foreignObject support
			const fo = document.implementation.hasFeature(
				"www.http://w3.org/TR/SVG11/feature#Extensibility",
				"1.1"
			);
			if (!fo) out = false;

			// if Safari
			const safari = !!navigator.userAgent.match(/Version\/[\d]+.*Safari/);
			if (safari) out = false;

			// if Edge
			const edge = navigator.userAgent.indexOf("Edge") > -1;
			if (edge) out = false;

			// if iOS
			const ios =
				/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
			if (ios) out = false;

			return out;
		}
	},
	methods: {
		async download() {
			if (!this.support) return;

			goatevent({
				path: location.host + `-single-download`,
				title: `single download`,
				event: true
			});

			this.downloading = true;
			await new Promise((resolve) => setTimeout(resolve, 300));
			const div = document.querySelector("#capture .image");

			const blob = await domtoimage.toBlob(div);

			const fileName = this.$store.state.fileName;
			const title = fileName
				? `${fileName}-${this.item.title}.png`
				: `${this.item.title}.png`;
			fileSaver.saveAs(blob, title);

			this.downloading = false;
		}
	},
	created() {
		this.$store.page(this.item.title);
		this.$store.desc(
			`Create a device mockup of your website or app with the ${
				this.item.company
			} ${this.item.title}`
		);

		if (document.querySelector(".hero-bottom")) {
			jump(".hero-bottom", {
				duration: 0,
				offset: 0
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.item {
	margin: 3.5em 0;

	@include res {
		margin: 2em 0;
	}
}
.title {
	text-align: center;
	font-size: 1.8em;
}

.settings {
	margin: 3em 0 4em;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;

	@include res {
		margin: 2em 0 3em;
		flex-direction: column;
		align-items: center;
	}

	&-box {
		width: 11em;
		text-align: center;

		@include res {
			margin-bottom: 1.5em;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	&-title {
		font-size: 0.9em;
		text-transform: uppercase;
		font-weight: 500;
		margin-bottom: 0.5em;
		margin-top: 0em;
	}
}

.image {
	transition: background-color 0.05s ease-in-out;

	&-wrap {
		position: relative;
		display: inline-flex;
		justify-content: center;
		width: 100%;
	}
}
.loading {
	display: flex;
	justify-content: center;
	margin-top: 8em;

	@include res {
		margin-top: 4em;
	}
}
.download {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 2em;
	height: 3em;

	.loading {
		font-size: 1.5em;
		margin-left: 0.5em;
	}

	.button {
		padding: 0.8em 1em;
	}
}

.support {
	text-align: center;
	color: $grey1;
	font-size: 0.75em;
	font-style: italic;
	margin: 1em 0;
	line-height: 1.8em;
}

.capture {
	position: fixed;
	top: 20em;
	right: -200em;
}

.spinner {
	width: 2em;
	height: 2em;
	position: relative;

	.double-bounce1,
	.double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: $green1;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.double-bounce2 {
		animation-delay: -1s;
	}
}

@keyframes sk-bounce {
	0%,
	100% {
		transform: scale(0);
	}
	50% {
		transform: scale(1);
	}
}

.download-enter-active,
.download-leave-active {
	transition: opacity 0.2s ease;
}
.download-enter,
.download-leave-to {
	opacity: 0;
}
</style>
